import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { ACCENT } from './offerteUi';
import { formatSyncTijd, euro } from './basisofferte';

const TYPE_LABEL = {
  'draaidag-script': 'draaidag', video: 'video', journaal: 'journaal', webinar: 'webinar', animatie: 'animatie',
};

/**
 * Beheer → Basisoffertes. De Gripp-basisoffertes (vlag "Basis" in Gripp) zijn de
 * bron van waarheid voor producten, prijzen en standaardopbouw van de generator.
 * Hier zie je wat er gespiegeld is en wanneer, en vernieuw je de spiegel.
 */
export default function BasisoffertesDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const [zoek, setZoek] = useState('');
  const pollRef = useRef(null);

  const laad = useCallback(async () => {
    setLoading(true); setFout(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/offertes/basisoffertes`);
      const d = await leesJson(r, 'Basisoffertes laden');
      setData(d);
      return d;
    } catch (e) { setFout(e.message || 'Basisoffertes laden mislukt'); return null; } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) laad(); }, [open, laad]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const vernieuw = async () => {
    setBezig(true); setFout(null);
    const vorige = data?.gesynct || '';
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/offertes/basisoffertes/vernieuwen`, { method: 'POST' });
      await leesJson(r, 'Basisoffertes vernieuwen');
    } catch (e) { meldFout(e, 'Basisoffertes vernieuwen'); setBezig(false); return; }
    // Gripp lezen duurt ~20-40 s (Gripp staat 2 calls per seconde toe). Poll tot
    // de spiegel een nieuwere synctijd heeft of de ronde met een fout stopt.
    let pogingen = 0;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      pogingen += 1;
      const d = await laad();
      const klaar = d && ((d.gesynct || '') > vorige);
      const mislukt = d?.status && !d.status.bezig && d.status.fout;
      if (klaar || mislukt || pogingen > 40) {
        clearInterval(pollRef.current);
        setBezig(false);
        if (mislukt && !klaar) setFout(d.status.fout);
        if (!klaar && !mislukt) setFout('Vernieuwen duurt langer dan verwacht; kijk over een paar minuten opnieuw.');
      }
    }, 4000);
  };

  const catalogus = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    const lijst = data?.catalogus || [];
    return q ? lijst.filter((c) => `${c.product} ${c.code} ${c.bron?.naam || ''}`.toLowerCase().includes(q)) : lijst;
  }, [data, zoek]);

  const basissen = (data?.basisoffertes || []).slice().sort((a, b) =>
    (b.leidendVoor.length - a.leidendVoor.length) || a.naam.localeCompare(b.naam));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '86vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="div">Basisoffertes uit Gripp</Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {data?.gesynct ? `Gesynchroniseerd ${formatSyncTijd(data.gesynct)}` : 'Nog niet gesynchroniseerd'}
            {' · '}elke nacht automatisch
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" variant="outlined" onClick={vernieuw} disabled={bezig}
            startIcon={bezig ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 17 }} />}
            sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT.dark, borderColor: '#c7d2fe' }}>
            {bezig ? 'Gripp lezen…' : 'Basisoffertes vernieuwen'}
          </Button>
          <IconButton size="small" onClick={onClose} aria-label="Sluiten"><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
        {loading && !data && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>}
        {data && !data.basisoffertes.length && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
            De spiegel is nog leeg. Klik op “Basisoffertes vernieuwen” om ze uit Gripp te lezen.
          </Typography>
        )}
        {data && data.basisoffertes.length > 0 && (
          <>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              Producten, prijzen, eenheden, inkoop, omschrijvingen en groepen komen uit deze offertes.
              Pas je een basisofferte in Gripp aan, dan neemt de generator dat na het vernieuwen over.
              Bij een conflict met oude vaste tarieven wint Gripp; alleen de overrides hieronder gaan vóór.
            </Typography>

            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em' }}>
              BASISOFFERTES ({basissen.length})
            </Typography>
            <Box sx={{ mb: 3, mt: 0.5 }}>
              {basissen.map((b) => (
                <Stack key={b.id} direction="row" spacing={1} alignItems="baseline"
                  sx={{ py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: b.leidendVoor.length ? 700 : 400 }}>
                    {b.naam}
                    {b.engels && <Typography component="span" variant="caption" sx={{ color: '#94a3b8' }}> · Engels</Typography>}
                  </Typography>
                  {b.leidendVoor.length > 0 && (
                    <Typography variant="caption" sx={{ color: ACCENT.dark, whiteSpace: 'nowrap' }}>
                      leidend voor {b.leidendVoor.map((t) => TYPE_LABEL[t] || t).join(', ')}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {b.aantalRegels} regels{b.bijgewerkt ? ` · Gripp ${formatSyncTijd(b.bijgewerkt, true)}` : ''}
                  </Typography>
                </Stack>
              ))}
            </Box>

            {(data.overrides || []).length > 0 && (
              <>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em' }}>
                  OVERRIDES (gaan vóór Gripp)
                </Typography>
                <Box sx={{ mb: 3, mt: 0.5 }}>
                  {data.overrides.map((o) => (
                    <Typography key={o.code} variant="body2" sx={{ py: 0.4 }}>
                      {o.product} · {o.code}: <b>{euro(o.prijs)}</b>/{o.eenheid}
                      <Typography component="span" variant="caption" sx={{ color: '#64748b' }}>
                        {' '}— {o.reden}{o.grippPrijs != null && Number(o.grippPrijs) !== Number(o.prijs)
                          ? ` (Gripp zegt ${euro(o.grippPrijs)})` : o.grippPrijs != null ? ' (gelijk aan Gripp)' : ''}
                      </Typography>
                    </Typography>
                  ))}
                </Box>
              </>
            )}

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em', flex: 1 }}>
                PRODUCTEN EN STANDAARDPRIJZEN ({catalogus.length})
              </Typography>
              <TextField size="small" variant="standard" placeholder="Zoek product of code" value={zoek}
                onChange={(e) => setZoek(e.target.value)} sx={{ width: 220 }} />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Prijs</TableCell>
                  <TableCell align="right">Inkoop</TableCell>
                  <TableCell>Uit basisofferte</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catalogus.map((c) => (
                  <TableRow key={`${c.code}-${c.productId}`}>
                    <TableCell>
                      {c.product}
                      <Typography component="span" variant="caption" sx={{ color: '#94a3b8' }}> · {c.code}</Typography>
                      {c.verborgen && <VisibilityOffOutlinedIcon titleAccess="Verborgen bij urenschrijven"
                        sx={{ fontSize: 14, color: '#94a3b8', ml: 0.5, verticalAlign: 'middle' }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {euro(c.prijs)}<Typography component="span" variant="caption" sx={{ color: '#94a3b8' }}>/{c.eenheid}</Typography>
                      {c.override && !c.override.gelijk && (
                        <Typography variant="caption" display="block" sx={{ color: '#b45309' }}>override</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{Number(c.inkoop) ? euro(c.inkoop) : ''}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>{c.bron?.naam}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
